import { createSlice } from "@reduxjs/toolkit";
import { ActionType } from "../actionTypes";
import { AddTablePageConfig } from "../reducer";
import {
  FullTableMap,
  TableLoadingMap,
  TableLogsMap,
  TableMap,
  TablePagesMap,
  TableRunControlMap,
  TableRunHistoryMap,
  TableRunResponses,
  TableSpecMap,
} from "../types/table";

export interface TablesState {
  tables: TableMap;
  tableSpecs: TableSpecMap;
  tablePages: TablePagesMap;
  tableRunControlMap: TableRunControlMap;
  tableRunResponses: TableRunResponses;
  tableRunHistoryMap: TableRunHistoryMap;
  tableLogsMap: TableLogsMap;
  tableLoadingMap: TableLoadingMap;
  fullTables: FullTableMap;
}

const initialState: TablesState = {
  tables: {},
  tableSpecs: {},
  tablePages: {},
  tableRunControlMap: {},
  tableRunResponses: {},
  tableRunHistoryMap: {},
  tableLogsMap: {},
  tableLoadingMap: {},
  fullTables: {},
};

const R = require("ramda");

export const tablesSlice = createSlice({
  name: "tables",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ADD_TABLES
    builder.addCase(ActionType.ADD_TABLES, (state, action: any) => {
      const tableEntries = R.map(
        (table: any) => [table["table-id"], table],
        action.tables,
      );
      const addTableObject = Object.fromEntries(tableEntries);
      return { ...state, tables: { ...state.tables, ...addTableObject } };
    });

    // ADD_TABLE_PAGE
    builder.addCase(ActionType.ADD_TABLE_PAGE, (state, action: any) => {
      const { tableId, index, rows } = action as AddTablePageConfig;
      const currentTablePagesEntry = state.tablePages[tableId] ?? {};
      const newTablePagesEntry = { ...currentTablePagesEntry, [index]: rows };
      return {
        ...state,
        tablePages: { ...state.tablePages, [tableId]: newTablePagesEntry },
      };
    });

    // SET_TABLE_SPECS
    builder.addCase(ActionType.SET_TABLE_SPECS, (state, action: any) => {
      return { ...state, tableSpecs: action.tableSpecs };
    });

    // SET_TABLE_RUN_CONTROL
    builder.addCase(ActionType.SET_TABLE_RUN_CONTROL, (state, action: any) => {
      return {
        ...state,
        tableRunControlMap: {
          ...state.tableRunControlMap,
          [action.tableId]: action.runControl,
        },
      };
    });

    // SET_TABLE_RUN_HISTORY_MAP
    builder.addCase(
      ActionType.SET_TABLE_RUN_HISTORY_MAP,
      (state, action: any) => {
        return { ...state, tableRunHistoryMap: action.tableRunHistoryMap };
      },
    );

    // SET_TABLE_LOGS_MAP
    builder.addCase(ActionType.SET_TABLE_LOGS_MAP, (state, action: any) => {
      return { ...state, tableLogsMap: action.tableLogsMap };
    });

    // SET_TABLE_RUN_RESPONSES
    builder.addCase(
      ActionType.SET_TABLE_RUN_RESPONSES,
      (state, action: any) => {
        return { ...state, tableRunResponses: action.tableRunResponses };
      },
    );

    // ADD_FULL_TABLES
    builder.addCase(ActionType.ADD_FULL_TABLES, (state, action: any) => {
      const { tables } = action;
      const tableMap = Object.fromEntries(
        tables.map((table: any) => [table["file-id"], table]),
      );
      return { ...state, fullTables: tableMap };
    });

    // SET_TABLE_LOADING
    builder.addCase(ActionType.SET_TABLE_LOADING, (state, action: any) => {
      const { tableId, loading } = action;
      return {
        ...state,
        tableLoadingMap: { ...state.tableLoadingMap, [tableId]: loading },
      };
    });
  },
});

export default tablesSlice.reducer;
